// client/src/components/AdminModeration.js
import React, { useEffect, useState } from 'react';
import { ref, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';
import AdminNavbar from './AdminNavbar';

const AdminModeration = () => {
  // On initialise activeFilter à null pour ne pas charger au démarrage.
  const [activeFilter, setActiveFilter] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState('initial'); // 'initial' ou 'final'
  const [selectedFile, setSelectedFile] = useState(null);

  // Fonction récursive pour récupérer les fichiers images dans le dossier correspondant au filtre.
  const fetchFilteredFiles = async (folderRef, filterFolderName) => {
    if (folderRef.name === filterFolderName) {
      try {
        const result = await listAll(folderRef);
        const imageItems = result.items.filter(item =>
          item.name.toLowerCase().endsWith('.png') ||
          item.name.toLowerCase().endsWith('.jpeg') ||
          item.name.toLowerCase().endsWith('.jpg')
        );
        const filePromises = imageItems.map(async (itemRef) => {
          const url = await getDownloadURL(itemRef);
          return { name: itemRef.name, url };
        });
        return await Promise.all(filePromises);
      } catch (error) {
        console.error('Erreur lors de la récupération des fichiers images:', error);
        return [];
      }
    } else {
      let filteredFiles = [];
      try {
        const result = await listAll(folderRef);
        for (const subFolderRef of result.prefixes) {
          const subFolderFiles = await fetchFilteredFiles(subFolderRef, filterFolderName);
          filteredFiles = filteredFiles.concat(subFolderFiles);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des sous-dossiers:', error);
      }
      return filteredFiles;
    }
  };

  const fetchFiles = async () => {
    if (!activeFilter) return; // Si aucun filtre n'est sélectionné, on ne charge rien.
    setLoading(true);
    const rootRef = ref(storage, '/');
    const allFiles = await fetchFilteredFiles(rootRef, activeFilter);
    setFiles(allFiles);
    setLoading(false);
  };

  // Utilisation d'un useEffect qui se déclenche uniquement si activeFilter est défini.
  useEffect(() => {
    if (activeFilter) {
      fetchFiles();
    }
    // eslint-disable-next-line
  }, [activeFilter]);


  const filterLabels = {
    'photos': 'Factures',
    'photo-profil': 'Photos profil',
    'pub': 'Promos'
  };

  // Gère le clic sur une image : ouvre la modale
  const handleImageClick = (file) => {
    setSelectedFile(file);
    setModalStep('initial');
    setModalOpen(true);
  };

  // Fonction pour supprimer l'image
  const handleDelete = async () => {
    if (!selectedFile) return;
    // On reconstruit le chemin en supposant que le fichier se trouve dans le dossier correspondant au filtre
    const fileRef = ref(storage, `${activeFilter}/${selectedFile.name}`);
    try {
      await deleteObject(fileRef);
      // Met à jour la liste en supprimant l'image supprimée
      setFiles(files.filter(file => file.name !== selectedFile.name));
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedFile(null);
    setModalStep('initial');
  };

  return (
    <div className="dashboard-container">
      <style>{`
        .am-page { width: 100%; max-width: var(--eb-content-max); display: flex; flex-direction: column; gap: 20px; }
        .dashboard-container .am-page .eb-page-title { margin: 0; text-align: left; }
        .am-page .eb-page-header { margin-bottom: 0; align-items: center; }
        .am-seg { display: inline-flex; flex-wrap: wrap; gap: 4px; padding: 4px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); }
        .am-seg button { min-height: 38px; padding: 0 16px; border: 0; border-radius: var(--eb-radius-sm); background: transparent; color: var(--eb-text-2); font-weight: 600; font-size: 14px; cursor: pointer; transition: background-color .15s ease, color .15s ease; }
        .am-seg button:hover { background: var(--eb-surface-2); color: var(--eb-text); }
        .am-seg button.is-active { background: var(--eb-brand); color: #fff; }
        .am-state { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 40px 20px; text-align: center; color: var(--eb-muted); font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 10px; margin: 0; }
        .am-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
        .am-tile { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); overflow: hidden; transition: box-shadow .15s ease, border-color .15s ease; }
        .am-tile:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); }
        .am-tile img { width: 100%; height: auto; display: block; cursor: pointer; }
        .am-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; padding: 16px; }
        .am-modal { width: 100%; max-width: 420px; background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg); padding: 24px; }
        .am-modal p { margin: 0 0 20px; font-size: 16px; font-weight: 600; color: var(--eb-text); }
        .am-modal__actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; }
        @media (max-width: 600px) {
          .am-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
          .am-seg { display: flex; width: 100%; }
          .am-seg button { flex: 1; }
          .am-modal__actions > button { flex: 1 1 100%; }
        }
      `}</style>
      <AdminNavbar />
      <div className="am-page">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Modération {activeFilter ? `${filterLabels[activeFilter]}` : ''}</h1>
            <p className="eb-page-subtitle">Cliquez sur une image pour la supprimer</p>
          </div>
          <div className="am-seg" role="tablist">
            <button className={activeFilter === 'photos' ? 'is-active' : ''} onClick={() => setActiveFilter('photos')}>Factures</button>
            <button className={activeFilter === 'photo-profil' ? 'is-active' : ''} onClick={() => setActiveFilter('photo-profil')}>Photos profil</button>
            <button className={activeFilter === 'pub' ? 'is-active' : ''} onClick={() => setActiveFilter('pub')}>Promos</button>
          </div>
        </header>

        {/* Message pour inviter l'utilisateur à choisir un filtre */}
        {!activeFilter ? (
          <p className="am-state">Veuillez choisir un filtre</p>
        ) : loading ? (
          <p className="am-state"><span className="eb-spinner eb-spinner--sm" /> Chargement...</p>
        ) : files.length === 0 ? (
          <p className="am-state">Aucun fichier trouvé dans les "{filterLabels[activeFilter]}"</p>
        ) : (
          <div className="am-grid">
            {files.map((file, index) => (
              <div key={index} className="am-tile">
                <img
                  src={file.url}
                  alt={file.name}
                  onClick={() => handleImageClick(file)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="am-overlay">
          <div className="am-modal" role="dialog" aria-modal="true">
            {modalStep === 'initial' && (
              <>
                <p>Voulez-vous supprimer la photo ?</p>
                <div className="am-modal__actions">
                  <button className="eb-btn eb-btn--secondary" onClick={closeModal}>Annuler</button>
                  <button className="eb-btn eb-btn--danger" onClick={() => setModalStep('final')}>Supprimer</button>
                </div>
              </>
            )}
            {modalStep === 'final' && (
              <>
                <p>Êtes-vous sûr ?</p>
                <div className="am-modal__actions">
                  <button className="eb-btn eb-btn--secondary" onClick={closeModal}>Annuler</button>
                  <button className="eb-btn eb-btn--danger" onClick={handleDelete}>Oui, supprimer</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminModeration;
